/**
 * Knowledge Bank Service
 * ======================
 * 
 * Frontend service for interacting with the Knowledge Bank API.
 * Allows storing and retrieving approved FMEA knowledge for reuse.
 */

import { authenticatedFetchJSON } from '../utils/authenticatedFetch';

// ============================================================================
// TYPES
// ============================================================================

export interface KnowledgeItem {
    id?: string;
    type: 'failure_mode' | 'cause' | 'control' | 'detection' | 'effect';
    category?: 'Man' | 'Machine' | 'Material' | 'Method';
    industry?: string;
    processType?: string;

    // Core content
    title: string;
    description?: string;

    // FMEA-specific fields
    severity?: number;
    occurrence?: number;
    detection?: number;
    rpn?: number;

    // Relationships
    relatedCauses?: string[];
    relatedControls?: string[];
    relatedEffects?: string[];

    // Metadata
    sourceProject?: string;
    sourceAiSuggestion?: boolean;
    aiConfidence?: number;
    usageCount?: number;
    approvedBy?: string;
    approvedAt?: string;
    createdBy?: string;
    createdAt?: string;
    organization?: string;
    fmeaStandard?: string;
    tags?: string[];
}

export interface KnowledgeSearchRequest {
    query?: string;
    type?: string;
    category?: string;
    industry?: string;
    processType?: string;
    fmeaStandard?: string;
    limit?: number;
}

export interface KnowledgeListResponse {
    items: KnowledgeItem[];
    total: number;
    filters?: Record<string, string | undefined>;
}

export interface KnowledgeCreateResponse {
    status: 'success' | 'duplicate';
    id?: string;
    item?: KnowledgeItem;
    message?: string;
    existingId?: string;
}

export interface KnowledgeBatchResponse {
    status: string;
    added: number;
    skipped: number;
    addedIds: string[];
    skippedItems: { title: string; reason: string }[];
}

export interface AiContextResponse {
    contextItems: Partial<KnowledgeItem>[];
    total: number;
    filters: Record<string, string | undefined>;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * List knowledge bank items with optional filtering
 */
export async function listKnowledgeItems(
    type?: string,
    category?: string,
    industry?: string,
    limit: number = 100
): Promise<KnowledgeListResponse> {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (category) params.append('category', category);
    if (industry) params.append('industry', industry);
    params.append('limit', limit.toString());

    return authenticatedFetchJSON<KnowledgeListResponse>(
        `/api/knowledge/items?${params.toString()}`
    );
}

/**
 * Create a new knowledge item
 */
export async function createKnowledgeItem(
    item: KnowledgeItem
): Promise<KnowledgeCreateResponse> {
    return authenticatedFetchJSON<KnowledgeCreateResponse>('/api/knowledge/items', {
        method: 'POST',
        body: JSON.stringify(item)
    });
}

/**
 * Batch create multiple knowledge items
 */
export async function createKnowledgeItemsBatch(
    items: KnowledgeItem[]
): Promise<KnowledgeBatchResponse> {
    return authenticatedFetchJSON<KnowledgeBatchResponse>('/api/knowledge/items/batch', {
        method: 'POST',
        body: JSON.stringify(items)
    });
}

/**
 * Search knowledge bank
 */
export async function searchKnowledge(
    request: KnowledgeSearchRequest
): Promise<{ results: KnowledgeItem[]; total: number; query?: string }> {
    return authenticatedFetchJSON('/api/knowledge/search', {
        method: 'POST',
        body: JSON.stringify(request)
    });
}

/**
 * Increment usage count for an item
 */
export async function incrementItemUsage(itemId: string): Promise<{ status: string; usageCount: number }> {
    return authenticatedFetchJSON(`/api/knowledge/items/${itemId}/use`, {
        method: 'PUT'
    });
}

/**
 * Delete a knowledge item
 */
export async function deleteKnowledgeItem(itemId: string): Promise<{ status: string; message: string }> {
    return authenticatedFetchJSON(`/api/knowledge/items/${itemId}`, {
        method: 'DELETE'
    });
}

/**
 * Get AI context items for prompt enrichment
 */
export async function getAiContext(
    processType?: string,
    industry?: string,
    fmeaStandard?: string,
    limit: number = 20
): Promise<AiContextResponse> {
    const params = new URLSearchParams();
    if (processType) params.append('processType', processType);
    if (industry) params.append('industry', industry);
    if (fmeaStandard) params.append('fmeaStandard', fmeaStandard);
    params.append('limit', limit.toString());

    return authenticatedFetchJSON<AiContextResponse>(
        `/api/knowledge/ai-context?${params.toString()}`
    );
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convert AI suggestion to Knowledge Item format
 */
export function aiSuggestionToKnowledgeItem(
    suggestion: {
        failureMode?: string;
        cause?: string;
        effect?: string;
        controlPrevention?: string;
        controlDetection?: string;
        severity?: number;
        occurrence?: number;
        detection?: number;
        category?: string;
    },
    metadata: {
        processType?: string;
        industry?: string;
        fmeaStandard?: string;
        sourceProject?: string;
    }
): KnowledgeItem[] {
    const items: KnowledgeItem[] = [];

    // Create failure mode item
    if (suggestion.failureMode) {
        items.push({
            type: 'failure_mode',
            title: suggestion.failureMode,
            category: suggestion.category as any,
            severity: suggestion.severity,
            occurrence: suggestion.occurrence,
            detection: suggestion.detection,
            rpn: (suggestion.severity || 1) * (suggestion.occurrence || 1) * (suggestion.detection || 1),
            relatedCauses: suggestion.cause ? [suggestion.cause] : [],
            relatedEffects: suggestion.effect ? [suggestion.effect] : [],
            relatedControls: [
                suggestion.controlPrevention,
                suggestion.controlDetection
            ].filter(Boolean) as string[],
            sourceAiSuggestion: true,
            ...metadata
        });
    }

    // Create cause item
    if (suggestion.cause) {
        items.push({
            type: 'cause',
            title: suggestion.cause,
            category: suggestion.category as any,
            sourceAiSuggestion: true,
            ...metadata
        });
    }

    // Create control items
    if (suggestion.controlPrevention) {
        items.push({
            type: 'control',
            title: suggestion.controlPrevention,
            description: 'Prevention control',
            sourceAiSuggestion: true,
            ...metadata
        });
    }

    if (suggestion.controlDetection) {
        items.push({
            type: 'detection',
            title: suggestion.controlDetection,
            description: 'Detection control',
            detection: suggestion.detection,
            sourceAiSuggestion: true,
            ...metadata
        });
    }

    return items;
}

/**
 * Save approved AI suggestions to Knowledge Bank
 */
export async function saveApprovedSuggestions(
    suggestions: any[],
    metadata: {
        processType?: string;
        industry?: string;
        fmeaStandard?: string;
        sourceProject?: string;
    }
): Promise<KnowledgeBatchResponse> {
    const items: KnowledgeItem[] = [];

    for (const suggestion of suggestions) {
        items.push(...aiSuggestionToKnowledgeItem(suggestion, metadata));
    }

    // Deduplicate by title
    const unique = items.filter((item, index, self) =>
        index === self.findIndex(t => t.title === item.title)
    );

    return createKnowledgeItemsBatch(unique);
}
